// eslint-disable-next-line import/no-cycle
import API from 'services/API';
import { Endpoints } from 'UI/constants/endpoints';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { PERSISTENCE_KEYS } from 'UI/constants/persistenceKeys';
import { hasFeatureFlag } from 'UI/utils/featureFlags';

const jwtDecode = token => {
  const base64url = token.split('.')[1];
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  return JSON.parse(atob(padded));
};

const loginWithAuthCode = async authCode => {
  const response = await API.post(Endpoints.Users, { code: authCode });

  if (!response?.data?.token || response?.status !== 200) {
    throw new Error(response);
  }

  const { token } = response.data;
  const decodedToken = jwtDecode(token.token);
  localStorage.setItem(PERSISTENCE_KEYS.ACCESS, JSON.stringify(token));
  API.defaults.headers.Authorization = `Bearer ${token.token}`;
  return decodedToken.data.user;
};

const getAccess = () => {
  const access = localStorage.getItem(PERSISTENCE_KEYS.ACCESS);
  return access && JSON.parse(access);
};

const getToken = () => {
  const access = getAccess();
  return access && access.token;
};

const getRefreshToken = () => {
  const access = getAccess();
  return access && access.refreshToken;
};

const getCurrentUser = () => {
  const access = getAccess();
  const decodedToken = access && jwtDecode(access.token);
  const tokenUser = decodedToken && decodedToken.data.user;

  if (!tokenUser || !hasFeatureFlag(FeatureFlags.UserDataByEndpoint)) return tokenUser;

  const userInfo = localStorage.getItem(PERSISTENCE_KEYS.USER_INFO);
  if (!userInfo) return tokenUser;

  try {
    const cachedUser = JSON.parse(userInfo);
    return cachedUser?.id === tokenUser.id ? cachedUser : tokenUser;
  } catch (error) {
    localStorage.removeItem(PERSISTENCE_KEYS.USER_INFO);
    return tokenUser;
  }
};

const isAuthenticated = () => {
  return getToken() !== null;
};

const deleteCrossDomainAuthCookie = () => {
  const authCookieKey = window.GPAC_ENV?.AUTH_COOKIE_KEY || process.env.REACT_APP_AUTH_COOKIE_KEY;
  if (!authCookieKey) {
    return;
  }

  const authDomain = window.location.hostname.split('.').slice(-2).join('.');
  document.cookie = `${authCookieKey}=; path=/; domain=.${authDomain}; Secure; SameSite=None; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
};

const logout = async () => {
  await API.get('users/logout');

  cleanLocalStorage();
  logoutMicrosoft();
  deleteCrossDomainAuthCookie();
};

const logoutMicrosoft = () => {
  const postLogoutUrl = `${
    (window.GPAC_ENV && window.GPAC_ENV.LOGOUT_REDIRECT_URL) || process.env.REACT_APP_REDIRECT_URL
  }`;

  window.location.replace(
    `${process.env.REACT_APP_MICROSOFT_URL}${process.env.REACT_APP_TENANT_URL}/oauth2/v2.0/logout?post_logout_redirect_uri=${postLogoutUrl}`
  );
};

const cleanLocalStorage = () => {
  localStorage.removeItem('notificationToken');
  localStorage.removeItem(PERSISTENCE_KEYS.ACCESS);
  localStorage.removeItem(PERSISTENCE_KEYS.USER_INFO);
  sessionStorage.clear();
};

export {
  cleanLocalStorage,
  deleteCrossDomainAuthCookie,
  getCurrentUser,
  getRefreshToken,
  getToken,
  isAuthenticated,
  loginWithAuthCode,
  logout
};
